import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
// tslint:disable-next-line:import-blacklist
import { UntypedFormControl, UntypedFormGroup, NgForm, ValidationErrors, Validators } from '@angular/forms';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { Subject, Subscription } from 'rxjs';

import { TinyMceService } from 'webcommon/common-api';
import {
  MacroActionValueType,
} from 'webcommon/shared';

import {MacroActionValueDtoExtended, MacroConfigDto, MacroInterfaces, MacroService} from '../ap-macro/index';
import { AutoCompleter, AutoCompleterSetupDto } from '../auto-completer/index';

const defaultToolbar = 'undo redo | styles | bullist numlist | bold strikethrough';
const embeddedToolbar = 'undo redo | styles | bullist | bold';
const textOnlyToolbar = 'undo redo';
// tslint:disable-next-line:max-line-length
const advancedToolbar = ' cut copy undo redo | alignleft aligncenter alignright | fontformats styles fontsize | bold italic underline strikethrough | bullist numlist |  removeformat | help | fullscreen | insertdatetime';

@Component({
  selector: 'ap-tiny-editor',
  templateUrl: './ap-tiny-editor.component.html',
})
export class ApTinyEditorComponent implements OnInit, OnDestroy, Validators {
  constructor(
    private ngZone: NgZone,
    private tinyMceService: TinyMceService) { }

  @ViewChild('editorInstance') editorInstance: EditorComponent;

  @Input() height: number;
  @Input() hideToolbar: boolean;
  @Input() hideStatusBar: boolean;
  @Input() expandTileOnWarning: boolean | undefined;
  @Input() removeFormatting: boolean;  // not used
  @Input() toolbarName: string | undefined;
  @Input() editorValue = '';
  @Input() disabled: boolean;
  @Input() editorName: string; // used as part of elementId, default value for formControlName, default value for displayName
  @Input() displayName: string;                 // used in invalid form text message, will use editorName if not provided
  @Input() macroService: MacroService | null;
  @Input() macroInterfaces: MacroInterfaces;
  @Input() textToInsert: Subject<string>;
  @Input() inboundError: Subject<string>;
  @Input() setFocusOnInit = true;               // sets the focus when the editor is initialized (useful for modal)
  @Input() setFocusOnInitCursorAtEnd = true;    // when setFocusOnInit is enabled, puts cursor at end of text
  @Input() required?: boolean;
  @Input() form?: NgForm;
  @Input() formGroup?: UntypedFormGroup;
  @Input() controlName?: string;                // formControlName: will use editorName if not provided
  @Input() showRequiredOnly = false;
  @Input() autoResize = false;
  @Input() autoResizeMaxHeight: number | null = null;
  @Input() autoResizeMinHeight: number | null = null;
  @Input() maxHeight: number | null = null;
  @Input() fontFamily = 'Roboto';
  @Input() pasteAsText = true;
  @Output() dirtyChanged: EventEmitter<void> = new EventEmitter();
  @Output() editorValueChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() editorValueChangedTextOnly: EventEmitter<string> = new EventEmitter<string>();
  @Output() blur: EventEmitter<void> = new EventEmitter();
  @Output() focus: EventEmitter<void> = new EventEmitter();

  defaultMinHeight = 120;
  defaultHeight = 400;

  loaded = false;
  tinymceOptions: any;
  autoCompleterSetupDto: AutoCompleterSetupDto;
  autoCompleter: AutoCompleter | null;
  insertSub: Subscription;
  errorSub: Subscription;
  editorId: string;
  hasWarning = false;
  showWarningSubject = new Subject<MacroActionValueDtoExtended>();
  useToolbar: string;
  warningMessage = '';
  apEditorFormControl: UntypedFormControl;
  minHeight = this.defaultMinHeight;
  private useSpellChecker = false;

  ngOnInit() {
    // Disable spell checker if readonly mode
    // use Tiny Spell Checker is asset valid
    // Otherwise use browser spell checker
    this.useSpellChecker = !this.disabled && this.tinyMceService.isSpellCheckEnabled();

    this.editorId = 'ap-tiny-editor-' + this.editorName;
    this.autoCompleterSetupDto = new AutoCompleterSetupDto();
    this.autoCompleterSetupDto.macroConfigDto = new MacroConfigDto();
    this.autoCompleterSetupDto.macroConfigDto.name = 'apEditorAutoCompleter';
    this.autoCompleterSetupDto.macroConfigDto.editorName = this.editorName;
    this.autoCompleterSetupDto.macroConfigDto.macroInterfaces = this.macroInterfaces;
    this.autoCompleterSetupDto.macroActionValue = {
      Action: MacroActionValueType.None,
      Value: null,
    };
    this.autoCompleterSetupDto.onWarningAdded = this.showWarningSubject;
    this.showWarningSubject.subscribe((value) => {
      this.updateWarningLine(value);
    });

    if (!this.hideToolbar) {
      switch (this.toolbarName) {
        case 'embeddedToolbar': {
          this.useToolbar = embeddedToolbar;
          break;
        }
        case 'advancedToolbar': {
          this.useToolbar = advancedToolbar;
          break;
        }
        case 'textOnlyToolbar': {
          this.useToolbar = textOnlyToolbar;
          break;
        }
        default: {
          this.useToolbar = defaultToolbar;
          break;
        }
      }
    }

    if (this.autoResize && this.autoResizeMaxHeight) {
      // use autosize plug to allow window to resize
      this.minHeight = this.autoResizeMinHeight ? this.autoResizeMinHeight : this.defaultMinHeight;
      this.maxHeight = this.autoResizeMaxHeight;
    } else {
      this.minHeight = this.defaultMinHeight;
      this.maxHeight = this.maxHeight ? this.maxHeight : this.height ? this.height : this.defaultHeight;
    }

    let toolbar = this.hideToolbar ? false : this.useToolbar;

    let pluginList = this.autoResize ? 'autoresize fullscreen lists' : 'fullscreen lists';
    if (this.useSpellChecker) {
      pluginList += ' tinymcespellchecker';
      if (!this.hideToolbar) {
        toolbar += ' | spellchecker  spellcheckdialog';
      }
    }

    this.tinymceOptions = {
      branding: false,
      browser_spellcheck: !this.useSpellChecker,
      elementpath: false,
      entity_encoding: 'raw',
      // format,
      height: this.height ? this.height : this.defaultHeight,
      max_height: this.maxHeight,
      menubar: false,
      min_height: this.minHeight,
      paste_as_text: this.pasteAsText,
      paste_data_images: false,
      plugins: pluginList,
      spellchecker_active: this.useSpellChecker,
      spellchecker_languages: this.useSpellChecker ? 'English=en_US_medical, Spanish=es' : '',
      remove_linebreaks: true,
      resize: true,
      readonly: '1',
      setup: (editor: any) => {
        editor.on('init', () => {
          if (this.macroService) {
            this.autoCompleter = AutoCompleter.create(this.macroService);
            this.autoCompleter.addAutoCompleter(editor, this.ngZone, this.autoCompleterSetupDto);
          }

          editor.getDoc().body.style.fontFamily = this.fontFamily ? this.fontFamily : 'Roboto';
          if (this.isRequired()) {
            editor.getDoc().body.style.backgroundColor = '#fafad2';
          }

          // set focus on editor and put cursor at end of text
          // this call is especially for the modal editors
          if (this.setFocusOnInit === true) {
            this.focusEditor(editor, this.setFocusOnInitCursorAtEnd);

            // for some reason this also refocuses the cursor. Moved to prevent auto focus when there's other fields
            editor.execCommand('mceAutoResize');
          }

          if (this.disabled) {
            editor.mode.set('readonly');
          }
        },
          editor.on('focus', () => {
            this.onFocus();
          }),
          editor.on('blur', () => {
            this.onBlur();
          }),
        );
      },
      statusbar: !this.hideStatusBar,
      style_formats: [
        { title: 'Heading 1', format: 'h1' },
        { title: 'Heading 2', format: 'h2' },
        { title: 'Heading 3', format: 'h3' },
        { title: 'Heading 4', format: 'h4' },
        { title: 'Heading 5', format: 'h5' },
        { title: 'Heading 6', format: 'h6' },
        { title: 'Paragraph', block: 'p' },
      ],
      toolbar,
      toolbar_mode: 'sliding',
    };

    this.insertSub = this.textToInsert && this.textToInsert.subscribe((x) => {
      this.editorInstance.editor.execCommand('mceInsertContent', false, x.replace(/(?:\r\n|\r|\n)/g, '<br>'));
    });

    this.errorSub = this.inboundError && this.inboundError.subscribe((error: string) => {
      this.autoCompleterSetupDto.macroActionValue.Action = MacroActionValueType.ShowWarning;
      this.autoCompleterSetupDto.macroActionValue.Value = error;
      this.updateWarningLine(this.autoCompleterSetupDto.macroActionValue);
    });

    this.initializeFormControl();

    this.loaded = true;
  }

  ngOnDestroy(): void {
    if (this.insertSub) {
      this.insertSub.unsubscribe();
    }
    if (this.errorSub) {
      this.errorSub.unsubscribe();
    }
    this.showWarningSubject.complete();
  }

  public getContent(): string {
    return this.editorValue;
  }
  public getContentAsText(): string {
    return this.editorInstance.editor.getContent({ format: 'text' });
  }
  onEditorValueChanged() {
    // At most only one of the events should emit
    // These methods should only be used when calling the getContent methods cannot be used.
    this.editorValueChanged.emit(this.editorValue);
    this.editorValueChangedTextOnly.emit(this.editorInstance.editor.getContent({ format: 'text' }));

    this.updateFormControl();
  }

  onDirty() {
    this.dirtyChanged.emit();
  }

  onFocus() {
    this.focus.next();
  }

  onBlur() {
    this.blur.next();
  }

  closeWarning() {
    this.autoCompleterSetupDto.macroActionValue.Action = MacroActionValueType.None;
    this.autoCompleterSetupDto.macroActionValue.Value = '';
    this.autoCompleterSetupDto.macroActionValue.MacroResultId = '';
    this.warningMessage = '';
    this.hasWarning = false;
  }

  showWarning() {
    // Warning text is shown for AutoCompleter, will need to refactor to support other warning types
    const show = this.autoCompleterSetupDto.macroActionValue.Action === MacroActionValueType.ShowWarning;
    return show;
  }

  updateWarningLine(macroActionValue: MacroActionValueDtoExtended) {
    this.warningMessage = macroActionValue.Value || '';
    const warning = macroActionValue.Action === MacroActionValueType.ShowWarning;
    if (warning) {
      if (!this.hasWarning) {
        this.hasWarning = true;
      }
    } else {
      if (this.hasWarning) {
        this.hasWarning = false;
      }
    }

    if (this.editorInstance && this.editorInstance.editor && this.editorInstance.editor.getContainer()) {
      if (warning && !this.expandTileOnWarning) {
        const calculatedHeight = (this.height ? this.height : this.defaultHeight) - 63;
        this.editorInstance.editor.getContainer().style.height = `${calculatedHeight}px`;
      } else {
        this.editorInstance.editor.getContainer().style.height = `${(this.height ? this.height : this.defaultHeight)}px`;
      }
    }
  }

  private focusEditor(editor: any, setCursorAtEnd: boolean = true) {
    editor.focus();
    if (setCursorAtEnd === true) {
      editor.selection.select(editor.getBody(), true);
      editor.selection.collapse(false);
    }
  }

  setFocus(setCursorAtEnd: boolean = true) {
    this.focusEditor(this.editorInstance.editor, setCursorAtEnd);
  }

  initializeFormControl() {
    if (!this.form) { return; } // if no form is given do not set this up

    this.apEditorFormControl = new UntypedFormControl(null, this.validate.bind(this));
    const controlName = this.controlName ? this.controlName : this.editorName;
    if (this.formGroup) {
      this.formGroup.addControl(controlName, this.apEditorFormControl);
      this.updateFormControl();
    } else {
      this.form.form.addControl(controlName, this.apEditorFormControl);
      this.updateFormControl();
    }
  }

  private updateFormControl() {
    if (!this.apEditorFormControl) { return; }
    this.apEditorFormControl.setValue(this.validate());
  }

  validate(): ValidationErrors | null {
    if (!this.editorValue) {
      if (this.isShowRequriedOnly()) return null;
    }
    const valid = this.getIsValid();
    return valid ? null : {
      required: true,
    };
  }

  isRequired(): boolean {
    const required = !this.required ? false : this.required;
    return !this.isShowRequriedOnly() && required;
  }
  isShowRequriedOnly(): boolean {
    return this.showRequiredOnly;
  }

  private getIsValid(): boolean {
    const isValid = !this.required || !!(this.editorValue);
    return isValid;
  }
}
